import React, { useEffect, useRef, useState } from 'react';
import { api, BACKEND } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Save, Loader2, KeyRound, AlertTriangle, Upload, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';

export default function AdminSettings() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pwd, setPwd] = useState({ current: '', new: '', confirm: '' });
    const [uploadingKey, setUploadingKey] = useState('');
    const fileRefs = { logo_url: useRef(null), logo_white_url: useRef(null), favicon_url: useRef(null), default_og_image: useRef(null) };
    const { user } = useAuth();

    useEffect(() => {
        api.get('/admin/settings').then(({ data }) => setSettings(data)).finally(() => setLoading(false));
    }, []);

    const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));
    const setSocial = (k, v) => setSettings(s => ({ ...s, social: { ...(s.social||{}), [k]: v } }));

    // Resolve a stored URL for preview display: absolute remains, relative gets
    // prefixed with BACKEND so admin preview works even if served from CRA dev.
    const toPreviewSrc = (u) => !u ? '' : (u.startsWith('http') ? u : `${BACKEND}${u}`);

    // Upload a file and write the resulting RELATIVE url into settings[fieldKey]
    const onLogoUpload = async (fieldKey, file) => {
        if (!file) return;
        setUploadingKey(fieldKey);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('alt', file.name.split('.')[0] || '');
            const { data } = await api.post('/admin/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            // data.url is always relative (/api/media/...) - safe across domains
            set(fieldKey, data.url);
            toast.success('Slika uploadana');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Upload nije uspio');
        } finally {
            setUploadingKey('');
            if (fileRefs[fieldKey]?.current) fileRefs[fieldKey].current.value = '';
        }
    };

    const onClearImage = (fieldKey) => {
        set(fieldKey, '');
        toast.success('Slika uklonjena (spremite postavke)');
    };

    const onSave = async () => {
        setSaving(true);
        try {
            await api.put('/admin/settings', settings);
            toast.success('Postavke spremljene');
        } catch { toast.error('Spremanje nije uspjelo'); } finally { setSaving(false); }
    };

    const onChangePwd = async () => {
        if (pwd.new !== pwd.confirm) { toast.error('Nove lozinke se ne podudaraju'); return; }
        if (pwd.new.length < 6) { toast.error('Lozinka mora imati najmanje 6 znakova'); return; }
        try {
            await api.post('/auth/change-password', { current_password: pwd.current, new_password: pwd.new });
            toast.success('Lozinka je promijenjena');
            setPwd({ current: '', new: '', confirm: '' });
        } catch (e) { toast.error(e?.response?.data?.detail || 'Promjena nije uspjela'); }
    };

    if (loading || !settings) return <p className="text-white/60">Učitavanje…</p>;

    return (
        <div className="max-w-4xl">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-medium">Postavke</h1>
                    <p className="text-sm text-white/60 mt-1">Globalne postavke sajta.</p>
                </div>
                <Button onClick={onSave} disabled={saving} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white">
                    {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Spremanje…</> : <><Save className="mr-2 h-4 w-4" /> Spremi</>}
                </Button>
            </div>

            <Tabs defaultValue="general" className="mt-6">
                <TabsList className="bg-[#161A21] flex-wrap h-auto">
                    <TabsTrigger value="general">Općenito</TabsTrigger>
                    <TabsTrigger value="contact">Kontakt</TabsTrigger>
                    <TabsTrigger value="seo">SEO Default</TabsTrigger>
                    <TabsTrigger value="seo_scripts" data-testid="tab-seo-scripts">SEO i Skripte</TabsTrigger>
                    <TabsTrigger value="social">Društvene mreže</TabsTrigger>
                    <TabsTrigger value="account">Račun</TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="mt-6">
                    <Card className="bg-[#111318] border-[#232A36] p-6 space-y-4">
                        <div><Label>Naziv firme</Label><Input value={settings.site_name || ''} onChange={(e) => set('site_name', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <div><Label>Slogan</Label><Input value={settings.site_tagline || ''} onChange={(e) => set('site_tagline', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <div><Label>Site URL</Label><Input value={settings.site_url || ''} onChange={(e) => set('site_url', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" placeholder="https://skobic-keramika.hr" /></div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <ImageField
                                label="Logo (glavni)"
                                value={settings.logo_url}
                                onChange={(v) => set('logo_url', v)}
                                onUpload={(f) => onLogoUpload('logo_url', f)}
                                onClear={() => onClearImage('logo_url')}
                                uploading={uploadingKey === 'logo_url'}
                                fileRef={fileRefs.logo_url}
                                toPreviewSrc={toPreviewSrc}
                                testidPrefix="settings-logo"
                            />
                            <ImageField
                                label="Logo (bijeli – za tamne pozadine)"
                                value={settings.logo_white_url}
                                onChange={(v) => set('logo_white_url', v)}
                                onUpload={(f) => onLogoUpload('logo_white_url', f)}
                                onClear={() => onClearImage('logo_white_url')}
                                uploading={uploadingKey === 'logo_white_url'}
                                fileRef={fileRefs.logo_white_url}
                                toPreviewSrc={toPreviewSrc}
                                testidPrefix="settings-logo-white"
                            />
                            <ImageField
                                label="Favicon"
                                value={settings.favicon_url}
                                onChange={(v) => set('favicon_url', v)}
                                onUpload={(f) => onLogoUpload('favicon_url', f)}
                                onClear={() => onClearImage('favicon_url')}
                                uploading={uploadingKey === 'favicon_url'}
                                fileRef={fileRefs.favicon_url}
                                toPreviewSrc={toPreviewSrc}
                                testidPrefix="settings-favicon"
                            />
                        </div>
                        <div>
                            <Label>Visina loga (px, desktop)</Label>
                            <Input
                                type="number"
                                min={24}
                                max={96}
                                value={settings.logo_height ?? 40}
                                onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    if (Number.isNaN(v)) { set('logo_height', 40); return; }
                                    set('logo_height', Math.max(24, Math.min(96, v)));
                                }}
                                className="mt-2 bg-[#0B0C0E] border-[#232A36] w-32"
                                data-testid="settings-logo-height-input"
                            />
                            <p className="text-xs text-white/55 mt-1">
                                Default 40px. Dozvoljeni raspon 24–96px. Mobilni uređaji koriste 75% ove vrijednosti automatski.
                            </p>
                        </div>
                        <div><Label>Radno vrijeme</Label><Input value={settings.working_hours || ''} onChange={(e) => set('working_hours', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                    </Card>
                </TabsContent>

                <TabsContent value="contact" className="mt-6">
                    <Card className="bg-[#111318] border-[#232A36] p-6 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>Telefon (display)</Label><Input value={settings.phone || ''} onChange={(e) => set('phone', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                            <div><Label>Telefon (tel: link)</Label><Input value={settings.phone_link || ''} onChange={(e) => set('phone_link', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono" /></div>
                        </div>
                        <div><Label>E-mail</Label><Input value={settings.email || ''} onChange={(e) => set('email', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>Grad</Label><Input value={settings.city || ''} onChange={(e) => set('city', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                            <div><Label>Regija</Label><Input value={settings.region || ''} onChange={(e) => set('region', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        </div>
                        <div><Label>Adresa (display)</Label><Input value={settings.address || ''} onChange={(e) => set('address', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <div><Label>Google Maps embed (iframe HTML)</Label><Textarea value={settings.google_maps_embed || ''} onChange={(e) => set('google_maps_embed', e.target.value)} rows={4} className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono text-xs" /></div>
                    </Card>
                </TabsContent>

                <TabsContent value="seo" className="mt-6">
                    <Card className="bg-[#111318] border-[#232A36] p-6 space-y-4">
                        <div><Label>Default SEO Title</Label><Input value={settings.default_seo_title || ''} onChange={(e) => set('default_seo_title', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <div><Label>Default Meta Description</Label><Textarea value={settings.default_seo_description || ''} onChange={(e) => set('default_seo_description', e.target.value)} rows={3} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <ImageField
                            label="Default OG Image (1200×630 preporučeno)"
                            value={settings.default_og_image}
                            onChange={(v) => set('default_og_image', v)}
                            onUpload={(f) => onLogoUpload('default_og_image', f)}
                            onClear={() => onClearImage('default_og_image')}
                            uploading={uploadingKey === 'default_og_image'}
                            fileRef={fileRefs.default_og_image}
                            toPreviewSrc={toPreviewSrc}
                            testidPrefix="settings-og-image"
                        />
                    </Card>
                </TabsContent>

                <TabsContent value="seo_scripts" className="mt-6">
                    <Card className="bg-[#111318] border-[#232A36] p-6 space-y-5" data-testid="seo-scripts-panel">
                        <div className="flex items-start gap-3 rounded-lg border border-yellow-900/60 bg-yellow-950/30 p-3">
                            <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0" />
                            <p className="text-xs text-yellow-200/80 leading-relaxed">
                                Ovo su napredne postavke. Skripte i robots.txt utječu na SEO i performanse cijelog sajta. Spremite samo provjereni sadržaj.
                            </p>
                        </div>

                        <div>
                            <Label>Canonical base URL</Label>
                            <Input
                                value={settings.canonical_base_url || ''}
                                onChange={(e) => set('canonical_base_url', e.target.value)}
                                placeholder="https://skobic-keramika.hr"
                                className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono"
                                data-testid="settings-canonical-base-url-input"
                            />
                            <p className="text-xs text-white/55 mt-1">
                                Koristi se za canonical tagove i URL-ove u sitemap.xml. Bez završnog “/”.
                            </p>
                        </div>

                        <div>
                            <Label>robots.txt sadržaj</Label>
                            <Textarea
                                value={settings.robots_txt || ''}
                                onChange={(e) => set('robots_txt', e.target.value)}
                                rows={10}
                                className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono text-xs"
                                placeholder={`User-agent: *\nDisallow: /admin\nDisallow: /api/\nAllow: /\n\nSitemap: https://skobic-keramika.hr/sitemap.xml`}
                                data-testid="settings-robots-txt-textarea"
                            />
                            <p className="text-xs text-white/55 mt-1">
                                Ostavite prazno za default. /robots.txt vraća ovaj tekst kao <code className="text-white/80">text/plain</code>.
                            </p>
                        </div>

                        <div>
                            <Label>Custom Head Scripts</Label>
                            <Textarea
                                value={settings.custom_head_scripts || ''}
                                onChange={(e) => set('custom_head_scripts', e.target.value)}
                                rows={8}
                                className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono text-xs"
                                placeholder={`<!-- Google Search Console -->\n<meta name="google-site-verification" content="..." />\n\n<!-- Google Tag Manager -->\n<script>...</script>`}
                                data-testid="settings-head-scripts-textarea"
                            />
                            <p className="text-xs text-white/55 mt-1">
                                Ubacuje se u <code className="text-white/80">&lt;head&gt;</code> svih javnih stranica. Podržava <code className="text-white/80">&lt;script&gt;</code>, <code className="text-white/80">&lt;meta&gt;</code>, <code className="text-white/80">&lt;link&gt;</code>.
                            </p>
                        </div>

                        <div>
                            <Label>Custom Footer Scripts</Label>
                            <Textarea
                                value={settings.custom_footer_scripts || ''}
                                onChange={(e) => set('custom_footer_scripts', e.target.value)}
                                rows={8}
                                className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono text-xs"
                                placeholder={`<!-- GTM noscript / chat widgets / async trackers -->\n<script>...</script>`}
                                data-testid="settings-footer-scripts-textarea"
                            />
                            <p className="text-xs text-white/55 mt-1">
                                Ubacuje se prije <code className="text-white/80">&lt;/body&gt;</code> svih javnih stranica.
                            </p>
                        </div>

                        <div className="pt-4 border-t border-[#232A36]">
                            <p className="text-xs uppercase tracking-[0.18em] text-white/55 mb-2">Provjera</p>
                            <ul className="text-xs text-white/70 space-y-1.5 leading-relaxed">
                                <li>
                                    <a href="/robots.txt" target="_blank" rel="noreferrer" className="underline text-[#B08D57]">/robots.txt</a> – mora vraćati plain text.
                                </li>
                                <li>
                                    <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="underline text-[#B08D57]">/sitemap.xml</a> – mora vraćati XML sa svim javnim stranicama, s canonical base URL-om.
                                </li>
                                <li>
                                    <a href="/api/robots.txt" target="_blank" rel="noreferrer" className="underline text-[#B08D57]">/api/robots.txt</a> i <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="underline text-[#B08D57]">/api/sitemap.xml</a> – sigurni fallback (direktni backend).
                                </li>
                            </ul>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="social" className="mt-6">
                    <Card className="bg-[#111318] border-[#232A36] p-6 space-y-4">
                        <div><Label>Facebook</Label><Input value={settings.social?.facebook || ''} onChange={(e) => setSocial('facebook', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <div><Label>Instagram</Label><Input value={settings.social?.instagram || ''} onChange={(e) => setSocial('instagram', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <div><Label>LinkedIn</Label><Input value={settings.social?.linkedin || ''} onChange={(e) => setSocial('linkedin', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <div><Label>YouTube</Label><Input value={settings.social?.youtube || ''} onChange={(e) => setSocial('youtube', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                    </Card>
                </TabsContent>

                <TabsContent value="account" className="mt-6">
                    <Card className="bg-[#111318] border-[#232A36] p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#B08D57]/20"><KeyRound className="h-5 w-5 text-[#B08D57]" /></div>
                            <div>
                                <h2 className="font-medium">Promjena lozinke</h2>
                                <p className="text-xs text-white/60">Email: {user?.email}</p>
                            </div>
                        </div>
                        <div><Label>Trenutna lozinka</Label><Input type="password" value={pwd.current} onChange={(e) => setPwd({...pwd, current: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <div><Label>Nova lozinka</Label><Input type="password" value={pwd.new} onChange={(e) => setPwd({...pwd, new: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <div><Label>Potvrdi novu lozinku</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd({...pwd, confirm: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        <Button onClick={onChangePwd} disabled={!pwd.current || !pwd.new} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white">Promijeni lozinku</Button>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}


/**
 * Reusable image upload field used in Settings (logo, white logo, favicon, OG image).
 * - Renders a preview if a value exists (relative or absolute URL).
 * - Lets admin upload a file (always stored as RELATIVE /api/media/... URL).
 * - Lets admin paste a URL directly (auto-sanitized server-side on save).
 * - Provides a "Remove" button that clears the value.
 */
function ImageField({ label, value, onChange, onUpload, onClear, uploading, fileRef, toPreviewSrc, testidPrefix }) {
    return (
        <div data-testid={`${testidPrefix}-field`}>
            <Label>{label}</Label>
            <div className="mt-2 rounded-lg border border-[#232A36] bg-[#0B0C0E] p-3 space-y-3">
                {/* Preview */}
                <div className="aspect-[4/3] rounded-md bg-[#0B0C0E] border border-[#232A36] flex items-center justify-center overflow-hidden">
                    {value ? (
                        <img
                            src={toPreviewSrc(value)}
                            alt={label}
                            className="max-h-full max-w-full object-contain"
                            data-testid={`${testidPrefix}-preview`}
                            onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                        />
                    ) : (
                        <span className="text-xs text-white/40">Nema slike</span>
                    )}
                </div>
                {/* URL input (manual paste, also editable) */}
                <Input
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="/api/media/… ili https://…"
                    className="bg-[#0B0C0E] border-[#232A36] text-xs font-mono"
                    data-testid={`${testidPrefix}-url-input`}
                />
                {/* Hidden file input + buttons */}
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onUpload(e.target.files?.[0])}
                    data-testid={`${testidPrefix}-file-input`}
                />
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="button"
                        size="sm"
                        onClick={() => fileRef?.current?.click()}
                        disabled={uploading}
                        className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white"
                        data-testid={`${testidPrefix}-upload-btn`}
                    >
                        {uploading ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" /> Upload…</> : <><Upload className="mr-2 h-3 w-3" /> Uploadaj</>}
                    </Button>
                    {value && (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={onClear}
                            className="border-red-900/60 text-red-400 hover:bg-red-900/20"
                            data-testid={`${testidPrefix}-clear-btn`}
                        >
                            <Trash2 className="mr-2 h-3 w-3" /> Ukloni
                        </Button>
                    )}
                </div>
                <p className="text-[11px] text-white/45 leading-relaxed">
                    Uploadi se spremaju u Emergent Object Store (perzistentno). Vrijednost u bazi je relativan URL <code className="text-white/70">/api/media/…</code> – preživljava promjenu domene i redeploy.
                </p>
            </div>
        </div>
    );
}
